import { useCallback, useMemo } from 'react'

import {
  Custom3dOutline,
  Texture,
  Image2,
  Hexagon3d,
  CubeDraft,
  Animate,
  ClockBackward,
  UserStar,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import useGetVariations from 'Pods/ThreeDStudio/Hooks/useGetVariations'
import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { STUDIO_EVENT, trackStudioEvent } from 'Pods/ThreeDStudio/Services/studioTracker'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { attachHistory, setActiveViewerSidebarTab } from 'Pods/ThreeDStudio/Store/actions/ui'
import { VIEWER_SIDEBAR_TAB } from 'Pods/ThreeDStudio/Store/constants'

// Analytics feature names for the ticket-defined editing tools; the remaining
// tabs report their tab id as-is.
const FEATURE_BY_TAB = {
  [VIEWER_SIDEBAR_TAB.REMESH]: 'simplification',
  [VIEWER_SIDEBAR_TAB.TEXTURE]: 'retexture',
  [VIEWER_SIDEBAR_TAB.SEGMENT]: 'segmentation',
}

const useViewerSidebar = (isModelOpened = false) => {
  const t = useTranslation()
  const isOpen = useThreeDStudioStore(state => state.ui?.isViewerSidebarOpen)
  const activeViewerSidebarTab = useThreeDStudioStore(state => state.ui?.activeViewerSidebarTab)
  const isUploadedModel = useThreeDStudioStore(state => !!state.modelSettings?.inputThreeDUrl)
  const generatorSessionId = useThreeDStudioStore(state => state.ui?.openedModel?.generatorSessionId)
  const isHistoryDetached = useThreeDStudioStore(state => !!state.ui?.isHistoryDetached)
  const isReadOnly = useOpenedModelReadOnly()
  const isEnhanceToolDisabled = !isModelOpened
  // Uploaded models have no generator session, so they can never have variations —
  // unless the upload was loaded from the variations tab of a still-opened session;
  // someone else's model exposes no variations the current user can manage.
  const isVariationsDisabled = isEnhanceToolDisabled || (isUploadedModel && !generatorSessionId) || isReadOnly
  // Shares the react-query cache with the variations tab, so the badge and the grid can
  // never disagree on what counts. Skipped while the tab is unreachable, which also keeps
  // the request off a non-owner's read-only session.
  const { variationsCount } = useGetVariations(isVariationsDisabled ? null : generatorSessionId)
  const isHistoryWindowOpen = isHistoryDetached && !isVariationsDisabled

  const openTab = useCallback((tab) => {
    // Re-clicking the active tab closes the panel (setActiveViewerSidebarTab
    // toggle semantics), which is not a feature "open".
    if (!isOpen || activeViewerSidebarTab !== tab) {
      trackStudioEvent(STUDIO_EVENT.EDITING_FEATURE_OPEN, {
        feature: FEATURE_BY_TAB[tab] ?? tab,
      })
    }

    setActiveViewerSidebarTab(tab)
  }, [isOpen, activeViewerSidebarTab])

  const handleRemeshClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    openTab(VIEWER_SIDEBAR_TAB.REMESH)
  }, [isEnhanceToolDisabled, openTab])

  const handleTextureClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    openTab(VIEWER_SIDEBAR_TAB.TEXTURE)
  }, [isEnhanceToolDisabled, openTab])

  const handleSegmentClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    openTab(VIEWER_SIDEBAR_TAB.SEGMENT)
  }, [isEnhanceToolDisabled, openTab])

  const handleAnimateClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    openTab(VIEWER_SIDEBAR_TAB.ANIMATE)
  }, [isEnhanceToolDisabled, openTab])

  const handleRenderClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    openTab(VIEWER_SIDEBAR_TAB.RENDER)
  }, [isEnhanceToolDisabled, openTab])

  const handleVariationsClick = useCallback(() => {
    if (isVariationsDisabled) {
      return
    }

    if (isHistoryWindowOpen) {
      attachHistory()
      return
    }

    openTab(VIEWER_SIDEBAR_TAB.VARIATIONS)
  }, [isVariationsDisabled, isHistoryWindowOpen, openTab])

  const handleAssistClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    openTab(VIEWER_SIDEBAR_TAB.ASSIST)
  }, [isEnhanceToolDisabled, openTab])

  const navigationItems = useMemo(() => {
    const variationsItem = {
      id: VIEWER_SIDEBAR_TAB.VARIATIONS,
      label: t('viewerSidebar.nav.history'),
      icon: ClockBackward,
      className: 'viewer-sidebar__item--separated',
      isHighlighted: isHistoryWindowOpen,
      onClick: handleVariationsClick,
      isDisabled: isVariationsDisabled,
      // An uploaded model is disabled for variations, but the user already has a
      // model open, so the "Generate or upload a model" tooltip would be misleading.
      // A non-owner gets an ownership-specific reason instead.
      hideDisabledTooltip: isUploadedModel && !isReadOnly,
      ...(isReadOnly && { disabledReason: t('viewerSidebar.nav.variationsNotOwnerReason') }),
      // The count is owner-only: a non-owner sees a read-only model with no
      // variations to manage, so the badge would be misleading.
      ...(!isReadOnly && variationsCount > 0 && { badgeCount: variationsCount }),
    }

    const items = [
      {
        id: 'viewer-sidebar',
        label: t('viewerSidebar.nav.enhance3dModel'),
        icon: Custom3dOutline,
        className: classnames(
          'viewer-sidebar__item--first',
          isOpen && 'viewer-sidebar__item--first-opened',
        ),
        isStatic: true,
      },
      {
        id: VIEWER_SIDEBAR_TAB.REMESH,
        label: t('viewerSidebar.nav.remesh'),
        icon: Hexagon3d,
        className: 'viewer-sidebar__item--second',
        onClick: handleRemeshClick,
        isDisabled: isEnhanceToolDisabled,
      },
      {
        id: VIEWER_SIDEBAR_TAB.TEXTURE,
        label: t('viewerSidebar.nav.texture'),
        icon: Texture,
        onClick: handleTextureClick,
        isDisabled: isEnhanceToolDisabled,
      },
      {
        id: VIEWER_SIDEBAR_TAB.SEGMENT,
        label: t('viewerSidebar.nav.segment'),
        icon: CubeDraft,
        onClick: handleSegmentClick,
        isDisabled: isEnhanceToolDisabled,
      },
      {
        id: VIEWER_SIDEBAR_TAB.ANIMATE,
        label: t('viewerSidebar.nav.animate'),
        icon: Animate,
        onClick: handleAnimateClick,
        isDisabled: isEnhanceToolDisabled,
      },
      {
        id: VIEWER_SIDEBAR_TAB.RENDER,
        label: t('viewerSidebar.nav.render'),
        icon: Image2,
        onClick: handleRenderClick,
        isDisabled: isEnhanceToolDisabled,
      },
    ]

    const assistItem = {
      id: VIEWER_SIDEBAR_TAB.ASSIST,
      label: t('viewerSidebar.nav.assist'),
      icon: UserStar,
      onClick: handleAssistClick,
      isDisabled: isEnhanceToolDisabled,
    }

    return [...items, assistItem, variationsItem]
  }, [
    t,
    isOpen,
    isEnhanceToolDisabled,
    isVariationsDisabled,
    isHistoryWindowOpen,
    isUploadedModel,
    isReadOnly,
    variationsCount,
    handleRemeshClick,
    handleTextureClick,
    handleSegmentClick,
    handleAnimateClick,
    handleRenderClick,
    handleVariationsClick,
    handleAssistClick,
  ])

  return {
    isOpen,
    navigationItems,
    isHistoryWindowOpen,
  }
}

export default useViewerSidebar
